import { Modal, Table, Button, Spinner } from "@heroui/react";
import { useQuery } from "@tanstack/react-query";
import { fetchCampaignActionDetails } from "@/entities/campaign/api/campaignApi";

export interface ActionDetailsModalProps {
  campaignId: string | number;
  actionId: string | null;
  onClose: () => void;
}

export const ActionDetailsModal = ({
  campaignId,
  actionId,
  onClose,
}: ActionDetailsModalProps) => {
  const { data, isLoading } = useQuery({
    queryKey: ["campaign-action-details", campaignId, actionId],
    queryFn: () => fetchCampaignActionDetails(campaignId, actionId!),
    enabled: Boolean(campaignId && actionId),
    staleTime: 60 * 1000,
  });

  return (
    <Modal
      isOpen={Boolean(actionId)}
      onOpenChange={(open: boolean) => {
        if (!open) onClose();
      }}
    >
      <Modal.Backdrop>
        <Modal.Container>
          <Modal.Dialog className="sm:max-w-3xl">
            <Modal.CloseTrigger />
            <Modal.Header>
              <Modal.Heading>
                {data?.rule_name || "Детали действия"}
              </Modal.Heading>
              <p className="text-xs text-neutral-400 mt-0.5">
                {data
                  ? new Date(data.created_at).toLocaleString("ru-RU", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : "Загрузка..."}
              </p>
            </Modal.Header>

            <Modal.Body className="flex flex-col gap-3">
              {data && (
                <p className="text-xs text-neutral-300">
                  {data.description}
                </p>
              )}

              {isLoading ? (
                <div className="py-12 flex items-center justify-center gap-2 text-neutral-400 text-xs">
                  <Spinner size="md" />
                  <span>Загрузка данных...</span>
                </div>
              ) : data?.items && data.items.length > 0 ? (
                <Table aria-label="Отключенные площадки">
                  <Table.ScrollContainer>
                    <Table.Content>
                      <Table.Header>
                        <Table.Column isRowHeader>Площадка</Table.Column>
                        <Table.Column>Причина отключения</Table.Column>
                        <Table.Column>Метрики</Table.Column>
                      </Table.Header>
                      <Table.Body>
                        {data.items.map((item) => (
                          <Table.Row key={item.id}>
                            <Table.Cell>
                              {item.target_entity}
                            </Table.Cell>
                            <Table.Cell>
                              {item.reason}
                            </Table.Cell>
                            <Table.Cell>
                              {item.details
                                ? [
                                    item.details.clicks != null
                                      ? `Клики: ${item.details.clicks}`
                                      : null,
                                    item.details.ctr != null
                                      ? `CTR: ${item.details.ctr}%`
                                      : null,
                                    item.details.cost != null
                                      ? `Расход: ${item.details.cost} ₽`
                                      : null,
                                  ]
                                    .filter(Boolean)
                                    .join(" | ") || "—"
                                : "—"}
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Content>
                  </Table.ScrollContainer>
                </Table>
              ) : (
                <div className="py-8 text-center text-xs text-neutral-400">
                  Нет записей по отключенным площадкам для данного действия.
                </div>
              )}
            </Modal.Body>

            <Modal.Footer>
              <Button slot="close" variant="tertiary" onClick={onClose}>
                Закрыть
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
};
